import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Row, Col, Typography, Space, Alert, Progress, Steps, message } from 'antd';
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  ApartmentOutlined,
  MailOutlined,
  PhoneOutlined,
  NumberOutlined,
  BankOutlined,
  ArrowRightOutlined,
  SafetyOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  SecurityScanOutlined,
  EditOutlined,
  SendOutlined,
  ClockCircleOutlined,
  SafetyCertificateOutlined,
  StarOutlined,
  RocketOutlined,
  LoadingOutlined,
  PushpinOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import { ValidationRules } from '../createCompanyPublic/validationRules';
import companyService from '../../../../../services/company/company.service';
import { showNotification } from '../createCompanyPublic/registrationHelpers';
import ReCAPTCHA from 'react-google-recaptcha'

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Step } = Steps;

interface CompanyFormProps {
  form: any;
  onFinish: (values: any) => void;
  loading: boolean;
}

const CompanyForm: React.FC<CompanyFormProps> = ({ form, onFinish, loading }) => {
  const [otpStep, setOtpStep] = useState<'form' | 'otp-sent' | 'otp-verified'>('form');
  const [otpLoading, setOtpLoading] = useState(false);
  const [companyData, setCompanyData] = useState<any>(null);
  const [countdown, setCountdown] = useState(0);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [formProgress, setFormProgress] = useState(0);
  const [validationStatus, setValidationStatus] = useState<{[key: string]: 'success' | 'error' | 'validating' | ''}>({});
  const [otpValues, setOtpValues] = useState<string[]>(['', '', '', '', '', '']);

  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);
  const [recaptchaExpired, setRecaptchaExpired] = useState(false);

   // reCAPTCHA callback functions
   const onRecaptchaChange = (value: string | null) => {
    setRecaptchaValue(value);
    setRecaptchaExpired(false);
    if (value) {
      // reCAPTCHA doğrulandı
      handleFieldValidation('recaptcha', 'success');
    } else {
      handleFieldValidation('recaptcha', 'error');
    }
  };

  const onRecaptchaExpired = () => {
    setRecaptchaValue(null);
    setRecaptchaExpired(true);
    handleFieldValidation('recaptcha', 'error');
    showNotification('warning', 'reCAPTCHA Süresi Doldu', 'Lütfen reCAPTCHA doğrulamasını yenileyin.');
  };

  // Form progress tracking
  const trackFormProgress = () => {
    const values = form.getFieldsValue();
    const requiredFields = ['name', 'email', 'phone', 'city', 'zipCode', 'taxNumber', 'taxOffice', 'address'];
    const filledFields = requiredFields.filter(field => values[field] && values[field].trim());
    const progress = Math.round((filledFields.length / requiredFields.length) * 100);
    setFormProgress(progress);
  };

  // Countdown timer for resend OTP
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // İlk form submit - OTP gönderme
  const handleInitialSubmit = async (values: any) => {

    if (!recaptchaValue) {
      showNotification('error', '🤖 reCAPTCHA Gerekli', 'Lütfen robot olmadığınızı doğrulayın.');
      return;
    }

    try {
      setOtpLoading(true);
      
      // Form verilerini kaydet
      setCompanyData(values);
      
      // OTP gönder
      const response = await companyService.sendOtp(values.phone);
      
      // SessionToken'ı kaydet
      if (response.sessionToken) {
        setSessionToken(response.sessionToken);
      }
      
      setOtpStep('otp-sent');
      setCountdown(180); // 180 saniye bekleme süresi
      showNotification('success', '📱 Doğrulama Kodu Gönderildi!', `${values.phone} numarasına doğrulama kodu gönderildi.`);
    } catch (error: any) {
      showNotification('error', '❌ Hata Oluştu', 'Doğrulama kodu gönderilirken bir hata oluştu. Lütfen telefon numaranızı kontrol edin.');
    } finally {
      setOtpLoading(false);
    }
  };

  // OTP doğrulama ve şirket oluşturma
  const handleOtpSubmit = async (values: any) => {
    // OTP değerini otpValues'dan al
    const otpString = otpValues.join('');
    
    if (!otpString || otpString.length !== 6) {
      showNotification('error', '⚠️ Geçersiz Kod', 'Lütfen 6 haneli doğrulama kodunu girin.');
      return;
    }

    if (!/^\d{6}$/.test(otpString)) {
      showNotification('error', '⚠️ Geçersiz Kod', 'Doğrulama kodu sadece rakamlardan oluşmalıdır.');
      return;
    }

    try {
      // Şirket oluşturma işlemini parent component'e gönder
      const finalData = {
        ...companyData,
        otp: otpString,
        sessionToken: sessionToken
      };
      
      setOtpStep('otp-verified');
      onFinish(finalData);
    } catch (error: any) {
      showNotification('error', '❌ Hata Oluştu', 'Doğrulama kodu kontrol edilirken bir hata oluştu.');
    }
  };

  // OTP tekrar gönderme
  const handleResendOtp = async () => {
    if (countdown > 0) return;
    
    try {
      setOtpLoading(true);
      const response = await companyService.sendOtp(companyData.phone);
      
      // Yeni sessionToken'ı kaydet
      if (response.sessionToken) {
        setSessionToken(response.sessionToken);
      }
      
      setCountdown(180);
      showNotification('success', '🔄 Kod Tekrar Gönderildi!', 'Yeni doğrulama kodu gönderildi.');
    } catch (error: any) {
      showNotification('error', '❌ Hata Oluştu', 'Kod tekrar gönderilirken bir hata oluştu.');
    } finally {
      setOtpLoading(false);
    }
  };

  // Telefon numarasını düzenleme
  const handleEditPhone = () => {
    setOtpStep('form');
    setCompanyData(null);
    setCountdown(0);
    setSessionToken(null);
    setOtpValues(['', '', '', '', '', '']);
    form.setFieldsValue({ otp: '' });
  };

  // Field validation handler
  const handleFieldValidation = (fieldName: string, status: 'success' | 'error' | 'validating' | '') => {
    setValidationStatus(prev => ({
      ...prev,
      [fieldName]: status
    }));
  };

  // Form adımına göre içerik render etme
  const renderFormContent = () => {
    if (otpStep === 'form') {
      return (
        <>
          <div className="step-header" style={{ 
            background: 'white', 
            color: '#2d3748',
            padding: '24px 0',
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              fontSize: '24px',
              color: 'white'
            }}>
              <ApartmentOutlined />
            </div>
            <Title level={2} 
            // className="step-title" 
            style={{ color: '#2d3748 !important', marginBottom: '8px' }}>
              Şirketinizi Kaydedin
            </Title>
            <Paragraph 
            // className="step-description" 
            style={{ color: '#4a5568 !important', fontSize: '16px', marginBottom: '0' }}>
              Başarının kapılarını aralayın! Şirket bilgilerinizi güvenle kaydedin ve Dinox'un tüm avantajlarından yararlanmaya hemen başlayın.
            </Paragraph>
            
            {/* Benefits Preview */}
             {/* <div className="benefits-preview" style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '20px' }}>
               <div className="benefit-item" style={{ 
                 display: 'flex', 
                 flexDirection: 'column', 
                 alignItems: 'center', 
                 padding: '15px', 
                 background: 'rgba(255,255,255,0.2)', 
                 borderRadius: '12px',
                 border: '1px solid rgba(255,255,255,0.3)'
               }}>
                 <RocketOutlined className="benefit-icon" style={{ 
                   fontSize: '20px', 
                   color: 'white', 
                   marginBottom: '8px',
                   background: 'rgba(255,255,255,0.2)',
                   padding: '8px',
                   borderRadius: '50%'
                 }} />
                 <span style={{ color: 'white', fontSize: '12px', fontWeight: 500 }}>Hızlı Kurulum</span>
               </div>
               <div className="benefit-item" style={{ 
                 display: 'flex', 
                 flexDirection: 'column', 
                 alignItems: 'center', 
                 padding: '15px', 
                 background: 'rgba(255,255,255,0.2)', 
                 borderRadius: '12px',
                 border: '1px solid rgba(255,255,255,0.3)'
               }}>
                 <SafetyCertificateOutlined className="benefit-icon" style={{ 
                   fontSize: '20px', 
                   color: 'white', 
                   marginBottom: '8px',
                   background: 'rgba(255,255,255,0.2)',
                   padding: '8px',
                   borderRadius: '50%'
                 }} />
                 <span style={{ color: 'white', fontSize: '12px', fontWeight: 500 }}>Güvenli Veri</span>
               </div>
               <div className="benefit-item" style={{ 
                 display: 'flex', 
                 flexDirection: 'column', 
                 alignItems: 'center', 
                 padding: '15px', 
                 background: 'rgba(255,255,255,0.2)', 
                 borderRadius: '12px',
                 border: '1px solid rgba(255,255,255,0.3)'
               }}>
                 <StarOutlined className="benefit-icon" style={{ 
                   fontSize: '20px', 
                   color: 'white', 
                   marginBottom: '8px',
                   background: 'rgba(255,255,255,0.2)',
                   padding: '8px',
                   borderRadius: '50%'
                 }} />
                 <span style={{ color: 'white', fontSize: '12px', fontWeight: 500 }}>Premium Destek</span>
               </div>
             </div> */}
          </div>

         



          <Form
            form={form}
            layout="vertical"
            onFinish={handleInitialSubmit}
            size="large"
            requiredMark="optional"
            className="modern-form"
            onValuesChange={trackFormProgress}
          >        
            <Row gutter={[24, 0]}>
              <Col xs={24}>
                <div className={`modern-form-item ${focusedField === 'name' ? 'focused' : ''} ${validationStatus.name === 'success' ? 'success' : ''}`}>
                  <Form.Item
                    name="name"
                    label={
                      <span style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        fontWeight: 600, 
                        fontSize: '15px', 
                        color: '#2d3748', 
                        marginBottom: '8px' 
                      }}>
                        <ApartmentOutlined style={{ color: '#667eea', fontSize: '16px' }} />
                        Şirket Adı
                        <span style={{ color: '#ff6b6b', fontWeight: 700, marginLeft: '4px' }}>*</span>
                      </span>
                    }
                    rules={ValidationRules.companyName}
                    hasFeedback
                    validateStatus={validationStatus.name}
                  >
                    <Input 
                      placeholder="Örn: Teknoloji Çözümleri A.Ş." 
                      showCount
                      maxLength={100}
                      onFocus={() => setFocusedField('name')}
                      onBlur={() => setFocusedField(null)}
                      style={{
                        borderRadius: '12px',
                        border: '2px solid #e2e8f0',
                        padding: '12px 16px',
                        fontSize: '16px',
                        background: 'white'
                      }}
                      suffix={validationStatus.name === 'success' ? <CheckCircleOutlined style={{ color: '#11998e', fontSize: '16px' }} /> : null}
                    />
                  </Form.Item>
                </div>
              </Col>

              <Col xs={24} lg={12}>
                <div className={`modern-form-item ${focusedField === 'email' ? 'focused' : ''} ${validationStatus.email === 'success' ? 'success' : ''}`}>
                  <Form.Item 
                    name="email" 
                    label={
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, fontSize: '15px', color: '#2d3748' }}>
                        <MailOutlined style={{ color: '#667eea', fontSize: '16px' }} />
                        Kurumsal E-posta
                        <span style={{ color: '#ff6b6b', fontWeight: 700, marginLeft: '4px' }}>*</span>
                      </span>
                    }
                    rules={ValidationRules.email}
                    hasFeedback
                    validateStatus={validationStatus.email}
                  >
                    <Input
                      placeholder="info@sirketiniz.com"
                      type="email"
                      onFocus={() => setFocusedField('email')}
                      onBlur={() => setFocusedField(null)}
                      style={{
                        borderRadius: '12px',
                        border: '2px solid #e2e8f0',
                        padding: '12px 16px',
                        fontSize: '16px',
                        background: 'white'
                      }}
                      suffix={validationStatus.email === 'success' ? <CheckCircleOutlined style={{ color: '#11998e', fontSize: '16px' }} /> : null}
                    />
                  </Form.Item>
                </div>
              </Col>

              <Col xs={24} lg={12}>
                <div className={`modern-form-item ${focusedField === 'phone' ? 'focused' : ''} ${validationStatus.phone === 'success' ? 'success' : ''}`}>
                  <Form.Item 
                    name="phone" 
                    label={
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, fontSize: '15px', color: '#2d3748' }}>
                        <PhoneOutlined style={{ color: '#667eea', fontSize: '16px' }} />
                        Telefon Numarası
                        <span style={{ color: '#ff6b6b', fontWeight: 700, marginLeft: '4px' }}>*</span>
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip id="tooltip-primary">SMS ile doğrulama kodu gönderilecektir</Tooltip>}
                        >
                          <InfoCircleOutlined style={{ color: '#4facfe', fontSize: '14px', marginLeft: '4px', cursor: 'help' }} />
                        </OverlayTrigger>
                      </span>
                    }
                    rules={ValidationRules.phone}
                    hasFeedback
                    validateStatus={validationStatus.phone}
                  >
                    <Input
                      placeholder="5XX XXX XX XX"
                      maxLength={20}
                      onFocus={() => setFocusedField('phone')}
                      onBlur={() => setFocusedField(null)}
                      style={{
                        borderRadius: '12px',
                        border: '2px solid #e2e8f0',
                        padding: '12px 16px',
                        fontSize: '16px',
                        background: 'white'
                      }}
                      suffix={validationStatus.phone === 'success' ? <CheckCircleOutlined style={{ color: '#11998e', fontSize: '16px' }} /> : null}
                    />
                  </Form.Item>
                </div>
              </Col>

              

              <Col xs={24} lg={12}>
                <div className={`modern-form-item ${focusedField === 'city' ? 'focused' : ''} ${validationStatus.city === 'success' ? 'success' : ''}`}>
                  <Form.Item 
                    name="city" 
                    label={
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, fontSize: '15px', color: '#2d3748' }}>
                        <EnvironmentOutlined style={{ color: '#667eea', fontSize: '16px' }} />
                        Şehir
                        <span style={{ color: '#ff6b6b', fontWeight: 700, marginLeft: '4px' }}>*</span>
                      </span>
                    }
                    rules={[{ required: true, message: "Şehir zorunludur" }]}
                    hasFeedback
                    validateStatus={validationStatus.city}
                  >
                    <Input
                      placeholder="Örn: İstanbul"
                      onFocus={() => setFocusedField('city')}
                      onBlur={() => setFocusedField(null)}
                      style={{
                        borderRadius: '12px',
                        border: '2px solid #e2e8f0',
                        padding: '12px 16px',
                        fontSize: '16px',
                        background: 'white'
                      }}
                      suffix={validationStatus.city === 'success' ? <CheckCircleOutlined style={{ color: '#11998e', fontSize: '16px' }} /> : null}
                    />
                  </Form.Item>
                </div>
              </Col>

              <Col xs={24} lg={12}>
                <div className={`modern-form-item ${focusedField === 'zipCode' ? 'focused' : ''} ${validationStatus.zipCode === 'success' ? 'success' : ''}`}>
                  <Form.Item 
                    name="zipCode" 
                    label={
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, fontSize: '15px', color: '#2d3748' }}>
                        <PushpinOutlined style={{ color: '#667eea', fontSize: '16px' }} />
                        Posta Kodu
                        <span style={{ color: '#ff6b6b', fontWeight: 700, marginLeft: '4px' }}>*</span>
                      </span>
                    }
                    rules={[{ required: true, message: "Posta kodu zorunludur" }, { pattern: /^\d{5}$/, message: 'Posta kodu 5 haneli bir sayı olmalıdır.' }]}
                    hasFeedback
                    validateStatus={validationStatus.zipCode}
                  >
                    <Input
                      placeholder="Örn: 34700"
                      maxLength={5}
                      onFocus={() => setFocusedField('zipCode')}
                      onBlur={() => setFocusedField(null)}
                      style={{
                        borderRadius: '12px',
                        border: '2px solid #e2e8f0',
                        padding: '12px 16px',
                        fontSize: '16px',
                        background: 'white'
                      }}
                      suffix={validationStatus.zipCode === 'success' ? <CheckCircleOutlined style={{ color: '#11998e', fontSize: '16px' }} /> : null}
                    />
                  </Form.Item>
                </div>
              </Col>

              <Col xs={24} lg={12}>
                <div className={`modern-form-item ${focusedField === 'taxNumber' ? 'focused' : ''} ${validationStatus.taxNumber === 'success' ? 'success' : ''}`}>
                  <Form.Item
                    name="taxNumber"
                    label={
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, fontSize: '15px', color: '#2d3748' }}>
                        <NumberOutlined style={{ color: '#667eea', fontSize: '16px' }} />
                        Vergi Numarası
                        <span style={{ color: '#ff6b6b', fontWeight: 700, marginLeft: '4px' }}>*</span>
                      </span>
                    }
                    rules={ValidationRules.taxNumber}
                    hasFeedback
                    validateStatus={validationStatus.taxNumber}
                  >
                    <Input
                      placeholder="1234567890"
                      maxLength={10}
                      onFocus={() => setFocusedField('taxNumber')}
                      onBlur={() => setFocusedField(null)}
                      style={{
                        borderRadius: '12px',
                        border: '2px solid #e2e8f0',
                        padding: '12px 16px',
                        fontSize: '16px',
                        background: 'white'
                      }}
                      suffix={validationStatus.taxNumber === 'success' ? <CheckCircleOutlined style={{ color: '#11998e', fontSize: '16px' }} /> : null}
                    />
                  </Form.Item>
                </div>
              </Col>

              <Col xs={24} lg={12}>
                <div className={`modern-form-item ${focusedField === 'taxOffice' ? 'focused' : ''} ${validationStatus.taxOffice === 'success' ? 'success' : ''}`}>
                  <Form.Item
                    name="taxOffice"
                    label={
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, fontSize: '15px', color: '#2d3748' }}>
                        <BankOutlined style={{ color: '#667eea', fontSize: '16px' }} />
                        Vergi Dairesi
                        <span style={{ color: '#ff6b6b', fontWeight: 700, marginLeft: '4px' }}>*</span>
                      </span>
                    }
                    rules={[{ required: true, message: "Vergi dairesi zorunludur" }]}
                    hasFeedback
                    validateStatus={validationStatus.taxOffice}
                  >
                    <Input 
                      placeholder="Örn: Kadıköy Vergi Dairesi"
                      onFocus={() => setFocusedField('taxOffice')}
                      onBlur={() => setFocusedField(null)}
                      style={{
                        borderRadius: '12px',
                        border: '2px solid #e2e8f0',
                        padding: '12px 16px',
                        fontSize: '16px',
                        background: 'white'
                      }}
                      suffix={validationStatus.taxOffice === 'success' ? <CheckCircleOutlined style={{ color: '#11998e', fontSize: '16px' }} /> : null}
                    />
                  </Form.Item>
                </div>
              </Col>

              <Col xs={24}>
                <div className={`modern-form-item ${focusedField === 'address' ? 'focused' : ''} ${validationStatus.address === 'success' ? 'success' : ''}`}>
                  <Form.Item 
                    name="address" 
                    label={
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, fontSize: '15px', color: '#2d3748' }}>
                        <span style={{ fontSize: '16px' }}>📍</span>
                        Şirket Adresi
                        <span style={{ color: '#ff6b6b', fontWeight: 700, marginLeft: '4px' }}>*</span>
                      </span>
                    }
                    rules={[
                      { required: true, message: "Şirket adresi zorunludur" },
                      { min: 10, message: "Adres en az 10 karakter olmalıdır" }
                    ]}
                    hasFeedback
                    validateStatus={validationStatus.address}
                  >
                    <TextArea
                      placeholder="Tam adresinizi yazın (mahalle, sokak, bina no, kat, daire)"
                      rows={3}
                      showCount
                      maxLength={500}
                      onFocus={() => setFocusedField('address')}
                      onBlur={() => setFocusedField(null)}
                      style={{
                        borderRadius: '12px',
                        border: '2px solid #e2e8f0',
                        padding: '12px 16px',
                        fontSize: '16px',
                        background: 'white'
                      }}
                    />
                  </Form.Item>
                </div>
              </Col>
            </Row>

            <Row gutter={[24, 0]} style={{ marginTop: '24px' }}>
              <Col xs={24}>
                <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '16px'}}>
                  <ReCAPTCHA
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6Ld0pGsrAAAAACNcb-eidd8pO95j8f7p6JGcHZKO'}
                    onChange={onRecaptchaChange}
                    onExpired={onRecaptchaExpired}
                    theme="light"
                    size="normal"
                    hl="tr" // Türkçe dil desteği
                  />
                </div>
                
                {recaptchaExpired && (
                  <Alert
                    message="reCAPTCHA süresi doldu"
                    description="Lütfen robot olmadığınızı tekrar doğrulayın."
                    type="warning"
                    showIcon
                    style={{ marginBottom: '16px', borderRadius: '12px' }}
                  />
                )}
              </Col>
            </Row>

            <div className="form-footer">
              <Button
                type="primary"
                htmlType="submit"
                icon={otpLoading ? <LoadingOutlined spin /> : <ArrowRightOutlined />}
                loading={otpLoading}
                size="large"
                block
                disabled={formProgress < 100}
                style={{
                  height: '56px',
                  fontSize: '18px',
                  fontWeight: 600,
                  borderRadius: '16px',
                  background: formProgress < 100 ? '#d1d5db' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  boxShadow: formProgress < 100 ? 'none' : '0 8px 24px rgba(102, 126, 234, 0.4)',
                  color: 'white'
                }}
              >
                {otpLoading ? (
                  <span style={{ color: 'white' }}>Gönderiliyor...</span>
                ) : (
                  <span style={{ color: 'white' }}>Devam Et</span>
                )}
              </Button>
            </div>
          </Form>
        </>
      );
    }

    if (otpStep === 'otp-sent') {
      return (
        <>
          <div className="step-header" style={{ 
            background: 'white', 
            color: '#2d3748',
            // padding: '24px 0',
            // marginBottom: '24px',
            textAlign: 'center'
          }}>
            {/* <div style={{
              width: '64px',
              height: '64px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              fontSize: '24px',
              color: 'white'
            }}>
              <SafetyOutlined />
            </div>
            <Title level={2} className="step-title" style={{ color: '#2d3748 !important', marginBottom: '8px' }}>
              Telefon Doğrulaması
            </Title>
            <Paragraph className="step-description" style={{ color: '#4a5568 !important', fontSize: '16px', marginBottom: '0' }}>
              <strong style={{ color: '#667eea' }}>{companyData?.phone}</strong> numarasına gönderilen 6 haneli kodu girin.
            </Paragraph> */}
          </div>



          {/* Enhanced Success Alert */}
          <Alert
            message={
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <CheckCircleOutlined style={{ color: '#11998e', fontSize: '24px' }} />
                <span style={{ fontSize: '18px', fontWeight: 600, color: '#11998e' }}>SMS Kodu Gönderildi!</span>
              </div>
            }
            description={
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <PhoneOutlined style={{ color: '#4facfe', fontSize: '20px' }} />
                  <div>
                    <Text strong style={{ color: '#2d3748', fontSize: '16px' }}>{companyData?.phone}</Text>
                    <br />
                    <Text style={{ color: '#718096', fontSize: '14px' }}>
                      {/* Kodu almadıysanız spam klasörünüzü kontrol edin veya birkaç dakika bekleyin. */}
                    </Text>
                  </div>
                </div>
                
                {/* {countdown > 0 && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    padding: '12px', 
                    background: 'rgba(79, 172, 254, 0.1)', 
                    borderRadius: '12px', 
                    border: '1px solid rgba(79, 172, 254, 0.2)' 
                  }}>
                    <ClockCircleOutlined style={{ color: '#4facfe', fontSize: '16px' }} />
                    <span style={{ fontSize: '14px', color: '#4a5568' }}>
                      Yeni kod talep edebilirsiniz: 
                      <strong style={{ color: '#667eea', fontWeight: 600, marginLeft: '8px' }}>
                        {Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')}
                      </strong>
                    </span>
                  </div>
                )} */}
              </div>
            }
            type="success"
            showIcon={false}
            style={{
              borderRadius: '16px',
              marginBottom: '30px',
              background: 'linear-gradient(135deg, rgba(17, 153, 142, 0.05), rgba(56, 239, 125, 0.05))',
              border: '2px solid rgba(17, 153, 142, 0.2)'
            }}
          />

          <Form
            layout="vertical"
            onFinish={handleOtpSubmit}
            size="large"
            className="otp-form enhanced"
          >
            <Row justify="center">
              <Col xs={24} sm={20} md={18} lg={16}>
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <i className="ti ti-message-2" style={{ color: 'white', fontSize: '18px' }}></i>
                    </div>
                    <span style={{ fontSize: '18px', fontWeight: 600, color: '#2d3748' }}>SMS Doğrulama Kodu</span>
                  </div>
                  <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>
                    Telefon numaranıza gönderilen 6 haneli kodu giriniz
                  </p>
                </div>

                <Form.Item
                  name="otp"
                  validateTrigger={['onSubmit']}
                  rules={[
                    { 
                      validator: (_, value) => {
                        const otpString = otpValues.join('');
                        if (!otpString || otpString.length === 0) {
                          return Promise.reject(new Error('Doğrulama kodu zorunludur'));
                        }
                        if (otpString.length !== 6) {
                          return Promise.reject(new Error('Doğrulama kodu 6 haneli olmalıdır'));
                        }
                        if (!/^\d{6}$/.test(otpString)) {
                          return Promise.reject(new Error('Sadece rakam girebilirsiniz'));
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                  className="otp-form-item"
                >
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <Input
                        key={index}
                        id={`otp-input-${index}`}
                        maxLength={1}
                        value={otpValues[index]}
                        style={{
                          width: '56px',
                          height: '56px',
                          fontSize: '24px',
                          fontWeight: 'bold',
                          textAlign: 'center',
                          borderRadius: '12px',
                          border: '2px solid #e2e8f0',
                          background: 'white',
                          color: '#2d3748',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }}
                        onChange={(e) => {
                          const value = e.target.value;
                          
                          // Sadece rakam girişine izin ver
                          if (!/^\d*$/.test(value)) {
                            return;
                          }
                          
                          // OTP values array'ini güncelle
                          const newOtpValues = [...otpValues];
                          newOtpValues[index] = value;
                          setOtpValues(newOtpValues);
                          
                          // Form değerini güncelle
                          const otpString = newOtpValues.join('');
                          form.setFieldsValue({ otp: otpString });
                          
                          // Otomatik ileri gitme
                          if (value && index < 5) {
                            const nextInput = document.getElementById(`otp-input-${index + 1}`);
                            if (nextInput) {
                              nextInput.focus();
                            }
                          }
                        }}
                        onKeyDown={(e) => {
                          // Backspace ile geri gitme
                          if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
                            const prevInput = document.getElementById(`otp-input-${index - 1}`);
                            if (prevInput) {
                              prevInput.focus();
                            }
                          }
                          
                          // Paste işlemi için
                          if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
                            e.preventDefault();
                            navigator.clipboard.readText().then(text => {
                              const digits = text.replace(/\D/g, '').slice(0, 6);
                              if (digits.length > 0) {
                                const newOtpValues = ['', '', '', '', '', ''];
                                for (let i = 0; i < Math.min(digits.length, 6); i++) {
                                  newOtpValues[i] = digits[i];
                                }
                                setOtpValues(newOtpValues);
                                form.setFieldsValue({ otp: newOtpValues.join('') });
                                
                                // Son dolu input'a focus
                                const lastIndex = Math.min(digits.length - 1, 5);
                                const lastInput = document.getElementById(`otp-input-${lastIndex}`);
                                if (lastInput) {
                                  lastInput.focus();
                                }
                              }
                            });
                          }
                        }}
                        onFocus={(e) => {
                          e.target.style.border = '2px solid #667eea';
                          e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.border = '2px solid #e2e8f0';
                          e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                        }}
                        placeholder="●"
                      />
                    ))}
                  </div>
                </Form.Item>

                {/* Yeniden Gönder Butonu */}
                {/* <div style={{ textAlign: 'center', marginTop: '20px' }}>
                  <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '12px' }}>
                    Kod gelmedi mi?
                  </p>
                  <Button
                    type="link"
                    style={{
                      color: '#667eea',
                      fontWeight: 600,
                      padding: '8px 16px',
                      height: 'auto',
                      fontSize: '14px'
                    }}
                    onClick={() => {
                      // SMS yeniden gönderme fonksiyonu buraya
                      message.info('SMS kodu yeniden gönderildi');
                    }}
                  >
                    <i className="ti ti-refresh me-1"></i>
                    Yeniden Gönder
                  </Button>
                </div> */}
              </Col>
            </Row>

            <div className="form-footer otp-footer">
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={loading ? <LoadingOutlined spin /> : <CheckCircleOutlined />}
                  loading={loading}
                  size="large"
                  block
                  style={{
                    height: '56px',
                    fontSize: '16px',
                    fontWeight: 600,
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                    border: 'none',
                    color: 'white',
                    boxShadow: '0 8px 24px rgba(17, 153, 142, 0.4)'
                  }}
                >
                  {loading ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '12px', color: 'white' }}>
                      <span style={{ color: 'white' }}>Şirket Oluşturuluyor</span>
                      <span style={{ display: 'flex', gap: '2px' }}>
                        <span style={{ color: 'white' }}>.</span>
                        <span style={{ color: 'white' }}>.</span>
                        <span style={{ color: 'white' }}>.</span>
                      </span>
                    </span>
                  ) : (
                    <span style={{ color: 'white' }}>Kodu Doğrula ve Şirketi Oluştur</span>
                  )}
                </Button>

                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  flexWrap: 'wrap', 
                  gap: '12px' 
                }}>
                  <Button
                    type="link"
                    onClick={handleResendOtp}
                    disabled={countdown > 0 || otpLoading}
                    loading={otpLoading}
                    icon={<SendOutlined />}
                    style={{
                      fontWeight: 500,
                      color: countdown > 0 || otpLoading ? '#a0aec0' : '#4facfe',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {countdown > 0 ? (
                      <span style={{ color: countdown > 0 ? '#a0aec0' : '#4facfe' }}>
                        Tekrar gönder ({countdown}s)
                      </span>
                    ) : (
                      <span style={{ color: '#4facfe' }}>Kodu Tekrar Gönder</span>
                    )}
                  </Button>
                  
                  <Button
                    type="link"
                    onClick={handleEditPhone}
                    icon={<EditOutlined />}
                    style={{
                      fontWeight: 500,
                      color: '#4facfe',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <span style={{ color: '#4facfe' }}>Telefon Numarasını Değiştir</span>
                  </Button>
                </div>
              </Space>
            </div>
          </Form>
        </>
      );
    }

    return null;
  };

  return (
    <Card 
      className="form-card modern-card enhanced" 
      loading={loading && otpStep === 'otp-verified'}
      style={{
        borderRadius: '24px',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        background: 'white',
        backdropFilter: 'blur(20px)',
        overflow: 'hidden',
        position: 'relative'
      }}
      bodyStyle={{ 
        padding: '40px',
        background: 'white'
      }}
    >
      {renderFormContent()}
    </Card>
  );
};

export default React.memo(CompanyForm);